// src/services/looker/lookerCsv.ts
/**
 * The whole /looker dashboard as one CSV: a section per drawn tile, in the
 * workspace's layout order, with the same hide rule the grid uses. A file leaves
 * the app without the caveats that sat beside the numbers, so the provenance
 * lines name the template, workspace, period and data date, and a cut Result
 * column is followed by the day it is counted through.
 *
 * NULL is an empty cell, never 0, except in an app box's figure column, where
 * it is the box's "—" (rules-04 D review I2); numbers stay numbers (Looker's raw
 * value), without binary float noise (withoutFloatNoise).
 *
 * @module services/looker/lookerCsv
 */
import type { TFunction } from 'i18next';
import { downloadCsv, type CsvCell } from '@/services/csv';
import { isHiddenWhenEmpty, type AppTileState } from '@/hooks/useLookerAppTiles';
import type { BackfillState } from '@/hooks/useLookerBackfill';
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { backfillTotal, type BackfillEta, type BackfillPlatform, type FreshChannel } from './lookerAppData';
import { platformLabel } from '@/utils/platformLabel';
import { csvEtaText } from './backfillEtaText';
import { uniqueLabels } from './lookerChatContext';
import { withoutFloatNoise } from './lookerNumber';
import { LOOKER_TILE_ROW_CAP, type LookerDashboardPayload, type LookerRow, type LookerTile, type TileTargetCell } from './lookerTypes';
import { formatCurrency } from '@/config/brand';
import { cprFieldOf, formatTargetDelta, hasVerdict, RESULT_METRIC_NAME, targetCellOf } from './lookerTargetCells';
import type { ResultMetric } from '../targets/resultMetric';
import type { LookerLayoutEntry } from './lookerLayout';
import type { CoverageNotesInput } from './lookerCoverageLabels';
import { badgeCoverageOf, coverageLinesOf, NO_TILE_NOTES, rowNotesByName, tileNotesOf, type TileNotes } from './lookerTileNotes';

/** Every sentence the file carries, already in the viewer's language. */
export interface DashboardCsvLabels {
  share: string;
  allWorkspaces: string;
  period: (start: string, end: string, prevStart: string, prevEnd: string) => string;
  dataAsOf: (date: string) => string;
  resultThrough: (date: string) => string;
  tileUnsupported: string;
  tileFailed: string;
  tileEmpty: string;
  tileTruncated: (count: number) => string;
  appFailed: string;
  needsWorkspace: string;
  loading: string;
  attentionPartial: string;
  /** The page's channel filter, by display name (app boxes still cover every channel). */
  channels: (list: string) => string;
  allChannels: string;
  /** The open history download, e.g. "Backfill: tiktok 0/1 (waiting 1)". */
  backfill: (list: string) => string;
  backfillFailed: string;
  backfillCount: (key: BackfillCountKey, n: number) => string;
  /** The whole window's ETA, e.g. "ETA ≥ 4 min" (lower bound) or "ETA ~4 min (includes …)" (queue counted); minutes null = "ETA unknown", never left out. */
  backfillEta: (eta: BackfillEta) => string;
  /** Each ad channel's sync, e.g. "Sync: facebook stale (last sync 2026-10-07 10:07 UTC, data through 2026-10-12)". */
  sync: (list: string) => string;
  syncFailed: string;
  syncState: (state: FreshChannel['state']) => string;
  syncLastSync: (at: string) => string;
  syncDataThrough: (date: string) => string;
  /** Why a channel is stale: how many days its newest data (or, with none, its last sync) lags today. */
  syncAge: (days: number, source: 'data' | 'sync') => string;
  /** The currency every number is in, e.g. "Currency: THB"; only when the server forced one (FR-X-02). */
  currency: (code: string) => string;
  /** Appended to the currency line when accounts of unknown currency were left out of every number. */
  currencyUnknownExcluded: string;
  /** DEC5 columns after Cost per Result. */
  targetColumn: string;
  targetColumnFor: (channel: string) => string;
  targetStatusColumn: string;
  /** The status word; none names the page currency when there is one; otherMetric names setAgainst. */
  targetStatus: (status: TileTargetCell['status'], currency: string | null, setAgainst?: ResultMetric) => string;
  /** A target that moved inside the period (amounts already formatted). */
  targetChanged: (from: string, to: string, on: string) => string;
  targetsAsOf: (date: string) => string;
  /** DEC3: Reach is in force; the spec's line, with its caveat. */
  resultMappingReach: string;
  /** DEC3: the definition could not be read; Result cells are empty. */
  resultMappingFailed: string;
}

/** Literal keys, so the locale integrity test sees them. */
const TARGET_STATUS: Record<TileTargetCell['status'], (t: TFunction) => string> = {
  met: (t) => t('looker.targets.status.met'),
  near: (t) => t('looker.targets.status.near'),
  over25: (t) => t('looker.targets.status.over25'),
  none: (t) => t('looker.targets.status.none'),
  na: (t) => t('looker.targets.status.na'),
  failed: (t) => t('looker.targets.status.failed'),
  noCurrency: (t) => t('looker.targets.status.noCurrency'),
  otherMetric: (t) => t('looker.targets.status.otherMetric'),
};

export type BackfillCountKey = 'waiting' | 'running' | 'stuck' | 'failed';

/** Literal keys, so the locale integrity test sees them. */
const BACKFILL_COUNT: Record<BackfillCountKey, (t: TFunction, n: number) => string> = {
  waiting: (t, n) => t('looker.backfillStatus.waiting', { n }),
  running: (t, n) => t('looker.backfillStatus.running', { n }),
  stuck: (t, n) => t('looker.backfillStatus.stuck', { n }),
  failed: (t, n) => t('looker.backfillStatus.failed', { n }),
};

/** Literal keys, so the locale integrity test sees them. */
const SYNC_STATE: Record<FreshChannel['state'], (t: TFunction) => string> = {
  ok: (t) => t('looker.dataStatus.csvState.ok'),
  stale: (t) => t('looker.dataStatus.csvState.stale'),
  error: (t) => t('looker.dataStatus.csvState.error'),
  never: (t) => t('looker.dataStatus.csvState.never'),
};

/** Literal keys, so the locale integrity test sees them. */
export const csvLabelsFrom = (t: TFunction): DashboardCsvLabels => ({
  share: t('looker.share'),
  allWorkspaces: t('looker.allWorkspaces'),
  period: (start, end, prevStart, prevEnd) => t('looker.periodLine', { start, end, prevStart, prevEnd }),
  dataAsOf: (date) => t('looker.dataAsOf', { date }),
  resultThrough: (date) => t('looker.resultThrough', { date }),
  tileUnsupported: t('looker.tileUnsupported'),
  tileFailed: t('looker.tileFailed'),
  tileEmpty: t('looker.tileEmpty'),
  tileTruncated: (count) => t('looker.tileTruncated', { count }),
  appFailed: t('looker.app.failed'),
  needsWorkspace: t('looker.app.needsWorkspace'),
  loading: t('looker.app.loading'),
  attentionPartial: t('looker.app.attention.truncated'),
  channels: (list) => t('looker.csvChannels', { list }),
  allChannels: t('looker.csvAllChannels'),
  backfill: (list) => t('looker.backfillStatus.csvLine', { list }),
  backfillFailed: t('looker.backfillStatus.csvFailed'),
  backfillCount: (key, n) => BACKFILL_COUNT[key](t, n),
  backfillEta: (eta) => csvEtaText(t, eta),
  sync: (list) => t('looker.dataStatus.csvLine', { list }),
  syncFailed: t('looker.dataStatus.csvFailed'),
  syncState: (state) => SYNC_STATE[state](t),
  syncLastSync: (at) => t('looker.dataStatus.csvLastSync', { at }),
  syncDataThrough: (date) => t('looker.dataStatus.csvDataThrough', { date }),
  syncAge: (days, source) => (source === 'data' ? t('looker.dataStatus.csvAgeData', { n: days }) : t('looker.dataStatus.csvAgeSync', { n: days })),
  currency: (code) => t('looker.currency.csvLine', { code }),
  currencyUnknownExcluded: t('looker.currency.csvUnknownExcluded'),
  targetColumn: t('looker.targets.column'),
  targetColumnFor: (channel) => t('looker.targets.columnFor', { channel }),
  targetStatusColumn: t('looker.targets.statusColumn'),
  targetStatus: (status, currency, setAgainst) => (
    status === 'none' && currency ? t('looker.targets.noneFor', { currency })
      : status === 'otherMetric' && setAgainst ? t('looker.targets.otherMetricFor', { metric: RESULT_METRIC_NAME[setAgainst] })
        : TARGET_STATUS[status](t)),
  targetChanged: (from, to, on) => t('looker.targets.changed', { from, to, on }),
  targetsAsOf: (date) => t('looker.targets.asOf', { date }),
  resultMappingReach: t('looker.resultMapping.csvReach'),
  resultMappingFailed: t('looker.resultMapping.failed'),
});

type Section = CsvCell[][];

/** A Looker cell as the file writes it: a non-integer number loses its float noise. */
const csvCell = (v: CsvCell): CsvCell => (typeof v === 'number' ? withoutFloatNoise(v) : v);

/** Card notes one row each; row chips one row per chip, naming its rows: "Unclassified: a; b". */
const noteRows = (tile: LookerTile, notes: TileNotes): Section => {
  const byChip = rowNotesByName(tile, tile.rows, notes).reduce<Record<string, string[]>>(
    (acc, [name, chips]) => chips.reduce((a, chip) => ({ ...a, [chip]: [...(a[chip] ?? []), name] }), acc),
    {},
  );
  return [
    ...notes.card.map((n) => [n]),
    ...Object.entries(byChip).map(([chip, names]) => [`${chip}: ${names.join('; ')}`]),
  ];
};

/** Where the two target columns go: right after Cost per Result; -1 = the tile has no targets. */
const targetAt = (tile: LookerTile): number => {
  const cpr = tile.targets ? cprFieldOf(tile) : null;
  return cpr ? tile.fields.findIndex((f) => f.name === cpr.name) + 1 : -1;
};

const insertAt = <T>(xs: readonly T[], at: number, extra: readonly T[]): T[] => (at < 0 ? [...xs] : [...xs.slice(0, at), ...extra, ...xs.slice(at)]);

const targetHeaders = (tile: LookerTile, labels: DashboardCsvLabels): string[] =>
  [tile.targets?.platform ? labels.targetColumnFor(platformLabel(tile.targets.platform)) : labels.targetColumn, labels.targetStatusColumn];

/** [target, status]: the target as a number (null = none), the status word with its delta. */
const targetCells = (tile: LookerTile, row: LookerRow, labels: DashboardCsvLabels): CsvCell[] => {
  const cell = targetCellOf(tile, row);
  if (!cell) return [null, null];
  const word = labels.targetStatus(cell.status, tile.targets?.currency ?? null, cell.setAgainst);
  const status = hasVerdict(cell) ? `${word} ${formatTargetDelta(cell.deltaPct)}` : word;
  // I1: on mixed keys the header is generic, so a channel's own target says so.
  const channel = cell.platform && !tile.targets?.platform ? ` (${platformLabel(cell.platform)})` : '';
  return [cell.target, `${status}${channel}`];
};

/** "conversion: Target changed during the period: ฿90.00 → ฿80.00 (2026-10-01)", one per changed objective. */
const targetChangeRows = (tile: LookerTile, labels: DashboardCsvLabels): Section => {
  const currency = tile.targets?.currency;
  // A null currency means noCurrency on every cell: no target, no change.
  if (!tile.targets || !currency) return [];
  const targets = tile.targets;
  const money = (n: number): string => formatCurrency(n, true, currency);
  return Object.values(targets.byObjective).flatMap((c) =>
    (c?.changed ? [[`${c.objective}: ${labels.targetChanged(money(c.changed.from), money(c.changed.to), c.changed.on)}`]] : []));
};

const looker = (tile: LookerTile, labels: DashboardCsvLabels, notes: TileNotes = NO_TILE_NOTES): Section => {
  if (tile.error) return [[tile.title], [tile.error === 'failed' ? labels.tileFailed : labels.tileUnsupported]];
  // The page and the chat carry a card note on an empty tile too, so the file does.
  if (tile.rows.length === 0) return [[tile.title], [labels.tileEmpty], ...noteRows(tile, notes)];
  return [
    [tile.title],
    // The chat's own column names, so a file and an answer name a column alike.
    insertAt(uniqueLabels(tile.fields, labels.share), targetAt(tile), targetHeaders(tile, labels)),
    ...tile.rows.map((r) => insertAt(tile.fields.map((f): CsvCell => csvCell(r[f.name]?.value ?? null)), targetAt(tile), targetCells(tile, r, labels))),
    ...(tile.truncated ? [[labels.tileTruncated(LOOKER_TILE_ROW_CAP)]] : []),
    ...(tile.resultThrough ? [[labels.resultThrough(tile.resultThrough)]] : []),
    ...targetChangeRows(tile, labels),
    ...noteRows(tile, notes),
  ];
};

/** Box 8's coverage section: a heading, then [platform, line] per line. Loading writes nothing (the button waits). */
export const coverageSection = (coverage: CoverageNotesInput | undefined): Section => {
  const lines = coverage ? coverageLinesOf(coverage.state, coverage.labels) : [];
  return coverage && lines.length > 0 ? [[coverage.labels.heading], ...lines.map((l) => [l.platform, l.text])] : [];
};

export const appCsvSection = (tile: Pick<LookerTile, 'title'>, state: AppTileState | undefined, labels: DashboardCsvLabels): Section => {
  if (state?.status === 'failed') return [[tile.title], [labels.appFailed]];
  if (state?.status === 'needsWorkspace') return [[tile.title], [labels.needsWorkspace]];
  if (state?.status !== 'ready') return [[tile.title], [labels.loading]];
  const { data } = state;
  // The chat sends rankingPartial whatever the row count, so the file notes it after the empty note too.
  const partial: Section = data.widget === 'attention' && data.truncated ? [[labels.attentionPartial]] : [];
  // Ready with no rows: the chat says "empty" for the same state.
  if (data.table.rows.length === 0) return [[tile.title], [labels.tileEmpty], ...partial];
  const figures = new Set(data.table.values ?? []);
  return [
    [tile.title],
    data.table.columns,
    ...data.table.rows.map((r) => data.table.columns.map((c): CsvCell => r[c] ?? (figures.has(c) ? '—' : null))),
    ...partial,
  ];
};

/** Title row, header row, data rows, notes, then a blank line — per drawn tile, in layout order. */
export const dashboardCsvRows = (
  payload: LookerDashboardPayload,
  appStates: Readonly<Record<string, AppTileState>>,
  layout: readonly Pick<LookerLayoutEntry, 'id'>[],
  labels: DashboardCsvLabels,
  /** The page's coverage (useLookerCoverage) and its sentences. Absent = no coverage notes. */
  coverage?: CoverageNotesInput,
): CsvCell[][] => {
  const byId = new Map(payload.tiles.map((t) => [t.id, t]));
  const data = badgeCoverageOf(coverage?.state, coverage?.channels);
  const notesOf = (tile: LookerTile): TileNotes =>
    coverage ? tileNotesOf(tile, data, payload.objective ?? null, coverage.labels) : NO_TILE_NOTES;
  return layout.flatMap((entry) => {
    const tile = byId.get(entry.id);
    if (!tile || isHiddenWhenEmpty(appStates[tile.id])) return [];
    const state = appStates[tile.id];
    const box8 = tile.app?.widget === 'dataQuality' && state?.status === 'ready' ? coverageSection(coverage) : [];
    const section = tile.type === 'app' ? [...appCsvSection(tile, state, labels), ...box8] : looker(tile, labels, notesOf(tile));
    return [...section, []];
  });
};

/** "tiktok 0/1 (waiting 1)": done over total, then only the non-zero open or bad counts. */
const backfillEntry = (p: BackfillPlatform, labels: DashboardCsvLabels): string => {
  const counts: Array<[BackfillCountKey, number]> = [['waiting', p.queued], ['running', p.running], ['stuck', p.stuck], ['failed', p.failed]];
  const notes = counts.filter(([, n]) => n > 0).map(([k, n]) => labels.backfillCount(k, n));
  return `${p.platform} ${p.done}/${backfillTotal(p)}${notes.length > 0 ? ` (${notes.join(', ')})` : ''}`;
};

/**
 * One line when a backfill is open or its read failed; nothing otherwise (none,
 * loading, all workspaces). The ETA closes the line, for the whole window.
 */
export const backfillLine = (state: BackfillState, labels: DashboardCsvLabels): string | null => {
  if (state.status === 'failed') return labels.backfillFailed;
  if (state.status !== 'ready' || state.platforms.length === 0) return null;
  const entries = state.platforms.map((p) => backfillEntry(p, labels));
  return labels.backfill([...entries, labels.backfillEta(state.eta)].join('; '));
};

const IDLE: BackfillState = { status: 'idle' };

/** "2026-10-07 10:07 UTC": a file has no viewer clock, so never "3 h ago". */
export const utcMinute = (iso: string): string => {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? iso : `${new Date(ms).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

/** "facebook stale (last sync …, data through …)"; the parenthesis only names what is known. */
const syncEntry = (c: FreshChannel, labels: DashboardCsvLabels): string => {
  const notes = [
    ...(c.lastSyncedAt ? [labels.syncLastSync(utcMinute(c.lastSyncedAt))] : []),
    ...(c.dataThrough ? [labels.syncDataThrough(c.dataThrough)] : []),
    ...(c.state === 'stale' && c.ageDays !== null && c.ageSource !== null ? [labels.syncAge(c.ageDays, c.ageSource)] : []),
  ];
  return `${c.platform} ${labels.syncState(c.state)}${notes.length > 0 ? ` (${notes.join(', ')})` : ''}`;
};

/** One line with ad channels or a failed read; nothing otherwise (none, loading, all workspaces). */
export const syncLine = (state: ChannelFreshnessState, labels: DashboardCsvLabels): string | null => {
  if (state.status === 'failed') return labels.syncFailed;
  if (state.status !== 'ready' || state.channels.length === 0) return null;
  return labels.sync(state.channels.map((c) => syncEntry(c, labels)).join('; '));
};

const SYNC_IDLE: ChannelFreshnessState = { status: 'idle' };

/** "Targets as of 2026-10-09" when any tile has targets; several dates are listed (tiles may differ, R2). */
const targetsAsOfLine = (payload: LookerDashboardPayload, labels: DashboardCsvLabels): string | null => {
  const dates = [...new Set(payload.tiles.flatMap((t) => (t.targets ? [t.targets.asOf] : [])))];
  return dates.length > 0 ? labels.targetsAsOf(dates.join(', ')) : null;
};

/** DEC3 (R18): the definition in force when it is not the plain default; a failed read says so. */
const resultMappingLine = (payload: LookerDashboardPayload, labels: DashboardCsvLabels): string | null => {
  const m = payload.resultMapping;
  if (!m) return null;
  if (m.failed) return labels.resultMappingFailed;
  return m.awareness === 'reach' ? labels.resultMappingReach : null;
};

export const dashboardProvenance = (
  payload: LookerDashboardPayload,
  workspaceName: string | null,
  labels: DashboardCsvLabels,
  /** The page's channel filter as ids; [] = every channel. */
  channels: readonly string[],
  backfill: BackfillState = IDLE,
  freshness: ChannelFreshnessState = SYNC_IDLE,
): string[] => {
  const p = payload.period;
  return [
    payload.template.title,
    payload.scope.kind === 'all' ? labels.allWorkspaces : workspaceName,
    p ? labels.period(p.start, p.end, p.previousStart, p.previousEnd) : null,
    payload.freshness.latestDataDate ? labels.dataAsOf(payload.freshness.latestDataDate) : null,
    payload.currency
      ? (payload.currencyUnknownExcluded ? `${labels.currency(payload.currency)}. ${labels.currencyUnknownExcluded}` : labels.currency(payload.currency))
      : null,
    resultMappingLine(payload, labels),
    syncLine(freshness, labels),
    channels.length > 0 ? labels.channels(channels.map((c) => platformLabel(c)).join(', ')) : labels.allChannels,
    backfillLine(backfill, labels),
    targetsAsOfLine(payload, labels),
  ].filter((line): line is string => Boolean(line));
};

export const downloadDashboardCsv = (
  payload: LookerDashboardPayload,
  appStates: Readonly<Record<string, AppTileState>>,
  layout: readonly Pick<LookerLayoutEntry, 'id'>[],
  workspaceName: string | null,
  labels: DashboardCsvLabels,
  channels: readonly string[],
  backfill: BackfillState = IDLE,
  freshness: ChannelFreshnessState = SYNC_IDLE,
  coverage?: CoverageNotesInput,
): void => {
  const start = payload.period?.start ?? 'all';
  const end = payload.period?.end ?? 'all';
  downloadCsv(`looker-${payload.template.id}_${start}_${end}.csv`, dashboardCsvRows(payload, appStates, layout, labels, coverage), {
    provenance: dashboardProvenance(payload, workspaceName, labels, channels, backfill, freshness),
  });
};
